/**
 * Password rules of the Cognito user pool (template.core.json UserPool PasswordPolicy):
 * at least 8 characters with an uppercase letter, a lowercase letter, a number and a symbol.
 * Sign-up, first-login (NEW_PASSWORD_REQUIRED) and password reset all check against this list,
 * so the form never accepts a password Cognito will reject.
 */

// Cognito's accepted symbols: ^ $ * . [ ] { } ( ) ? " ! @ # % & / \ , > < ' : ; | _ ~ ` = + -
const SYMBOL = /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/;

export interface PasswordRule {
  id: 'length' | 'uppercase' | 'lowercase' | 'number' | 'symbol';
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'uppercase', label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'At least one number', test: (p) => /[0-9]/.test(p) },
  { id: 'symbol', label: 'At least one special character', test: (p) => SYMBOL.test(p) },
];

/** The rules the password does not meet yet (empty when it is valid). */
export function unmetPasswordRules(password: string): PasswordRule[] {
  return PASSWORD_RULES.filter((rule) => !rule.test(password));
}

export function isPasswordValid(password: string): boolean {
  return unmetPasswordRules(password).length === 0;
}
